import {
  Box,
  Text,
  Image,
  // Stack,
  Button as CButton,
  Icon,
  Flex,
  Divider,
  Menu,
  useDisclosure,
  MenuButton,
  MenuList,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  Tag,
} from '@chakra-ui/react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Lightbox from 'react-image-lightbox';
import { Link, useParams } from 'react-router-dom';

import 'react-image-lightbox/style.css';

import { IconBriefcase, IconClip, IconClock, IconComment, IconFileClock } from 'assets/icons';
import Button from 'components/Button';
import LogoLoader from 'components/Loader/LogoLoader';
import Title from 'components/Title';
import Tooltip from 'components/Tooltip';
import FEATURES from 'global/features';
import { routePaths } from 'global/routePaths';
// import { useFetchSegmentedTimesheetById } from 'hooks/timesheet/useFetchSegmentedTimesheet';
import { useFetchTimesheetById } from 'hooks/timesheet/useFetchTimesheetById';
import { useFeatureList } from 'store/useFeatureStore';
import { formatDistance, formatToLongLocalizedDate, formatTwelveHourMinuteTime } from 'utils/format';
import { convertSecondsToMinutesRoundDown } from 'utils/helpers';

import Segments from './Segments';

const SegmentedTracking: React.FC<unknown> = () => {
  const { t } = useTranslation(['common', 'timesheetMileage']);

  const featureList: any = useFeatureList();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const { id } = useParams();

  const [lightBox, setLightBox] = useState<{ photoIndex: number; isOpen: boolean }>({ photoIndex: 0, isOpen: false });

  const { status, data } = useFetchTimesheetById(id!);

  if (status === 'loading') return <LogoLoader />;

  const differingClockInOut = {
    clockIn:
      data?.payload?.timezone?.clock_in && data?.payload?.timezone?.company_tz !== data?.payload?.timezone?.clock_in,
    clockOut:
      data?.payload?.timezone?.clock_out && data?.payload?.timezone?.company_tz !== data?.payload?.timezone?.clock_out,
  };

  return (
    <Box>
      <Flex alignItems="center" justifyContent="space-between" gridGap="8" flexWrap="wrap">
        <Flex alignItems={{ md: 'center' }} flexDirection={{ base: 'column', md: 'row' }}>
          <Title>
            {t('timesheetMileage:entryFor')} {data?.payload?.first_name} {data?.payload?.last_name}
          </Title>
          <Text
            fontWeight="semibold"
            listStyleType="disc"
            fontSize="md"
            ml="5"
            position="relative"
            lineHeight="1.3"
            _after={{
              position: 'absolute',
              top: 'calc(62% - 4px)',
              left: '-10px',
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              background: 'gray.300',
              content: '""',
            }}
          >
            <span>
              {data?.payload?.clock_in?.local_time
                ? formatToLongLocalizedDate(data?.payload?.clock_in?.local_time)
                : null}
            </span>
            <span> - </span>
            <span>
              {data?.payload?.clock_out?.local_time
                ? formatToLongLocalizedDate(data?.payload?.clock_out?.local_time)
                : null}
            </span>
          </Text>
        </Flex>
        <Flex flexWrap="wrap" gridGap="3">
          <div>
            {featureList[FEATURES.photo_attachments] && (
              <Menu isLazy closeOnBlur autoSelect={false} isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
                <MenuButton
                  as={CButton}
                  bg="rgba(72,82, 175, 0.1)"
                  color="primary.500"
                  fontWeight="semibold"
                  _hover={{
                    bg: 'rgba(72,82, 175, 0.2)',
                  }}
                  _active={{
                    bg: 'rgba(72,82, 175, 0.2)',
                  }}
                  leftIcon={<Icon as={IconClip} boxSize="16px" />}
                >
                  {t('common:attachments')} ({data?.payload?.attachment_files.length})
                </MenuButton>
                <MenuList maxHeight="350px" width="305px" overflow="hidden" p="2">
                  {Array.isArray(data?.payload?.attachment_files) && data?.payload?.attachment_files.length ? (
                    <Flex display="flex" flexWrap="wrap">
                      {data?.payload?.attachment_files.map((item: any, index: any) => (
                        <Tooltip label={item.caption} key={item.id}>
                          <Image
                            src={item.imageUrl}
                            alt={`attachment-${item.id}`}
                            boxSize="85px"
                            m="1"
                            objectFit="cover"
                            cursor="pointer"
                            borderRadius="6px"
                            border="1px solid"
                            borderColor="gray.300"
                            onClick={() => setLightBox({ isOpen: true, photoIndex: index })}
                          />
                        </Tooltip>
                      ))}
                      {/* </Stack> */}
                    </Flex>
                  ) : null}
                </MenuList>
              </Menu>
            )}
          </div>
          {data?.payload?.notes ? (
            <Popover placement="bottom">
              <PopoverTrigger>
                <CButton
                  bg="rgba(72,82, 175, 0.1)"
                  color="primary.500"
                  fontWeight="semibold"
                  _hover={{
                    bg: 'rgba(72,82, 175, 0.2)',
                  }}
                  _active={{
                    bg: 'rgba(72,82, 175, 0.2)',
                  }}
                  leftIcon={<Icon as={IconComment} sx={{ '& path': { fill: 'newBlue.500' } }} />}
                >
                  {t('common:notes')}
                </CButton>
              </PopoverTrigger>
              <PopoverContent>
                <PopoverArrow />
                <PopoverCloseButton />
                <PopoverBody>{data?.payload?.notes}</PopoverBody>
              </PopoverContent>
            </Popover>
          ) : null}
          <Link to={routePaths.timesheetAndMileage.editTimesheet.route.replace(':id', data?.payload?.id)}>
            <Button
              bg="secondary.50"
              color="secondary.400"
              _hover={{
                bg: 'secondary.100',
              }}
              _active={{
                bg: 'secondary.100',
              }}
            >
              {t('timesheetMileage:updateTimesheet')}
            </Button>
          </Link>
        </Flex>
      </Flex>
      {data?.payload?.flagged_reason && (
        <Flex mt="3" alignItems="center">
          <Tag bg="secondary.100" size="lg" color="secondary.600" fontSize="sm" fontWeight="semibold" flex="0 0 auto">
            {t('timesheetMileage:flagged')}
          </Tag>
          <Text ml="2" color="newGray.800" fontWeight="semibold">
            {data?.payload?.flagged_reason}
          </Text>
        </Flex>
      )}
      <Divider my="6" borderStyle="dashed" />
      <Flex flexWrap="wrap" gridGap="3">
        <Flex alignItems="center" border="1px solid" borderColor="newBlue.100" borderRadius="base" p="4">
          <Flex
            justifyContent="center"
            alignItems="center"
            width="40px"
            height="40px"
            background="newBlue.100"
            borderRadius="50%"
            mr="4"
          >
            <Icon as={IconBriefcase} boxSize="16px" sx={{ '& path': { fill: 'newBlue.500' } }} />
          </Flex>
          <Box fontSize="sm" fontWeight="semibold">
            <Text color="newGray.800" fontSize="12px" textTransform="uppercase">
              {t('common:job')}
            </Text>
            <Text fontWeight="bold">{data?.payload?.job_name}</Text>
          </Box>
        </Flex>
        <Flex alignItems="center" border="1px solid" borderColor="newBlue.100" borderRadius="base" p="4">
          <Flex
            justifyContent="center"
            alignItems="center"
            width="40px"
            height="40px"
            background="newBlue.100"
            borderRadius="50%"
            mr="4"
          >
            <Icon as={IconFileClock} boxSize="16px" sx={{ '& path': { fill: 'newBlue.500' } }} />
          </Flex>
          <Box fontSize="sm" fontWeight="semibold">
            <Text color="newGray.800" fontSize="12px" textTransform="uppercase">
              {t('common:task')}
            </Text>
            <Text fontWeight="bold">{data?.payload?.task_name || '-'}</Text>
          </Box>
        </Flex>
        <Flex gridGap="8" border="1px solid" borderColor="newBlue.100" borderRadius="base" p="4" flexWrap="wrap">
          <Flex alignItems="flex-start">
            <Flex
              justifyContent="center"
              alignItems="center"
              width="40px"
              height="40px"
              background="newBlue.100"
              borderRadius="50%"
              mr="4"
            >
              <Icon as={IconClock} boxSize="16px" sx={{ '& path': { fill: 'newBlue.500' } }} />
            </Flex>
            <Box fontSize="sm" fontWeight="semibold">
              <Text color="newGray.800" fontSize="12px" textTransform="uppercase">
                {t('common:duration')}
              </Text>
              <Text fontWeight="bold">{data?.payload?.duration}</Text>
            </Box>
          </Flex>
          <Flex alignItems="flex-start">
            <Box fontSize="sm" fontWeight="semibold">
              <Text color="newGray.800" fontSize="12px" textTransform="uppercase">
                {t('common:clockIn')}
              </Text>
              <Text fontWeight="bold">
                {data?.payload?.clock_in?.local_time
                  ? formatTwelveHourMinuteTime(data?.payload?.clock_in?.local_time)
                  : '-'}
              </Text>
              {differingClockInOut.clockIn ? (
                <Tag bg="secondary.100" size="sm" color="secondary.600" fontSize="xs" fontWeight="semibold">
                  {data?.payload?.timezone?.clock_in || ''}
                </Tag>
              ) : null}
            </Box>
          </Flex>
          <Flex alignItems="flex-start">
            <Box fontSize="sm" fontWeight="semibold">
              <Text color="newGray.800" fontSize="12px" textTransform="uppercase">
                {t('common:clockOut')}
              </Text>
              <Text fontWeight="bold">
                {data?.payload?.clock_out?.local_time
                  ? formatTwelveHourMinuteTime(data?.payload?.clock_out?.local_time)
                  : '-'}
              </Text>
              {differingClockInOut.clockOut ? (
                <Tag bg="secondary.100" size="sm" color="secondary.600" fontSize="xs" fontWeight="semibold">
                  {data?.payload?.timezone?.clock_out || ''}
                </Tag>
              ) : null}
            </Box>
          </Flex>
          <Flex alignItems="flex-start">
            <Box fontSize="sm" fontWeight="semibold">
              <Text color="newGray.800" fontSize="12px" textTransform="uppercase">
                {t('common:break')}
              </Text>
              <Text fontWeight="bold">
                {data?.payload?.break_in_seconds
                  ? convertSecondsToMinutesRoundDown(data?.payload.break_in_seconds) + ' min'
                  : 0}
              </Text>
            </Box>
          </Flex>
          <Flex alignItems="flex-start">
            <Box fontSize="sm" fontWeight="semibold">
              <Text color="newGray.800" fontSize="12px" textTransform="uppercase">
                {t('common:mileage')}
              </Text>
              <Text fontWeight="bold">{data?.payload?.mileage ? formatDistance(data?.payload?.mileage) : 0}</Text>
            </Box>
          </Flex>
        </Flex>
      </Flex>

      {lightBox.isOpen ? (
        <Lightbox
          mainSrc={data?.payload?.attachment_files[lightBox.photoIndex].imageUrl}
          nextSrc={
            data?.payload?.attachment_files[(lightBox.photoIndex + 1) % data?.payload?.attachment_files.length].imageUrl
          }
          prevSrc={
            data?.payload?.attachment_files[
              (lightBox.photoIndex + data?.payload?.attachment_files.length - 1) %
                data?.payload?.attachment_files.length
            ].imageUrl
          }
          imageCaption={data?.payload?.attachment_files[lightBox.photoIndex].caption}
          onCloseRequest={() => setLightBox({ ...lightBox, isOpen: false })}
          onMovePrevRequest={() => {
            const newImageIndex =
              (lightBox.photoIndex + data?.payload?.attachment_files.length - 1) %
              data?.payload?.attachment_files.length;
            setLightBox({ ...lightBox, photoIndex: newImageIndex });
          }}
          onMoveNextRequest={() => {
            const newImageIndex = (lightBox.photoIndex + 1) % data?.payload?.attachment_files.length;
            setLightBox({ ...lightBox, photoIndex: newImageIndex });
          }}
        />
      ) : null}
      <Segments jobId={data?.payload?.job_id} />
    </Box>
  );
};

export default SegmentedTracking;
